import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { navItems } from '../../data/site';

type MobileNavigationProps = { open: boolean; onClose: () => void };

export function MobileNavigation({ open, onClose }: MobileNavigationProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 bg-ink/90 px-5 py-5 backdrop-blur-xl lg:hidden"
          id="mobile-navigation"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <div className="mx-auto flex max-w-shell justify-between">
            <Link
              to="/"
              onClick={onClose}
              className="focus-ring flex items-center gap-2 text-sm font-bold tracking-[0.12em] text-white"
            >
              <img
                src="/branding/crimsonsky-symbol.png"
                alt=""
                className="h-8 w-8 object-contain"
                aria-hidden="true"
              />
              CRIMSONSKY
            </Link>
            <button
              ref={closeButtonRef}
              className="focus-ring rounded-lg p-2 text-slate-300 hover:bg-slate-800"
              onClick={onClose}
              aria-label="Close navigation"
            >
              <X size={22} />
            </button>
          </div>
          <motion.nav
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.25, delay: 0.04 }}
            className="mx-auto mt-16 flex max-w-shell flex-col gap-2"
            aria-label="Mobile primary navigation"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={({ isActive }) =>
                  `focus-ring flex items-center justify-between border-b border-slate-800 py-4 font-display text-2xl font-semibold ${isActive ? 'text-white' : 'text-slate-500'}`
                }
              >
                {item.label}
                <ArrowUpRight size={20} aria-hidden="true" />
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={onClose}
              className="focus-ring mt-8 inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-crimson-500 px-5 text-sm font-semibold text-white"
            >
              Discuss your project <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </motion.nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
